import { Button, Card, List, ListItem, ListItemText } from "@/components/ui";
import { APP_DATE_FORMAT_EXPANDED } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCourseQuiz } from "@/shared/model/edo-course-quiz.model";
import { defaultValue, IEdoQuizAttempt } from "@/shared/model/edo-quiz-attempt.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getEntity as getCourseQuizEntity } from "@/shared/reducers/entities/edo-course-quiz.reducer";
import { createEntity as createQuizAttemptEntity, getEntities as getAttempts } from "@/shared/reducers/entities/edo-quiz-attempt.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { AxiosResponse } from "axios";
import { useEffect } from "react";
import { TextFormat, Translate } from "react-jhipster";
import { useNavigate, useParams } from "react-router-dom";
import UserAttemptRow from "./components/UserAttemptRow";

const StartQuizRoute = () => {
    const params = useParams<{ courseQuizId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { entity: courseQuiz } = useAppSelector<EntityState<IEdoCourseQuiz>>(state => state.edoCourseQuiz);
    const { entities: userAttempts } = useAppSelector<EntityState<IEdoQuizAttempt>>(state => state.edoQuizAttempt);
    const { account } = useAppSelector<AuthenticationState>(state => state.authentication);

    const getUserAttempts = () => {
        const filters = new URLSearchParams();
        filters.append("filter.user.id", '$eq:' + account.id);
        filters.append("filter.courseQuiz", '$eq:' + params.courseQuizId);
        dispatch(getAttempts({
            sort: "updatedAt,DESC",
            filters: filters.toString(),
        }));
    };

    useEffect(() => {
        if (params.courseQuizId) {
            dispatch(getCourseQuizEntity(params.courseQuizId));
            getUserAttempts();
        }
    }, [params.courseQuizId]);

    const handleStartQuiz = () => {
        dispatch(createQuizAttemptEntity({
            ...defaultValue,
            courseQuiz,
        })).then((response) => {
            const id = (response.payload as AxiosResponse<{ data: IEdoCourseQuiz }>).data.data.id;
            navigate(`/quiz-attempts/${id}`);
        });
    };

    return (
        <Card className="surface p-6 text-center">
            <div className="mb-6">
                <h2 className="text-heading-2 font-semibold tracking-tight text-[var(--color-mockup-text)]">
                    {courseQuiz?.quiz?.title}
                </h2>
            </div>
            <div className="mb-6">
                <p className="whitespace-pre-wrap text-body text-[var(--color-mockup-text-mid)]">
                    {courseQuiz?.quiz?.description}
                </p>
            </div>
            {courseQuiz?.availableAttempts ? (
                <div className="mb-6">
                    <p className="text-body text-[var(--color-mockup-text-mid)]">
                        Tentativi a disposizione: {courseQuiz.availableAttempts}
                    </p>
                </div>
            ) : null}
            {courseQuiz?.duration ? (
                <div className="mb-6">
                    <p className="text-body font-medium text-[var(--color-mockup-text)]">Durata</p>
                    <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                        {Math.trunc(courseQuiz.duration / 60)} ore, {Math.trunc(courseQuiz.duration % 60)} minuti
                    </p>
                </div>
            ) : null}
            {courseQuiz?.timeOpen ? (
                <div className="mb-6">
                    <p className="text-body font-medium text-[var(--color-mockup-text)]">Disponibile dal</p>
                    <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                        <TextFormat
                            type="date"
                            value={courseQuiz.timeOpen}
                            format={APP_DATE_FORMAT_EXPANDED}
                            locale="it"
                        />
                    </p>
                </div>
            ) : null}
            {courseQuiz?.timeClose ? (
                <div className="mb-6">
                    <p className="text-body font-medium text-[var(--color-mockup-text)]">Termina il</p>
                    <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                        <TextFormat
                            type="date"
                            value={courseQuiz.timeClose}
                            format={APP_DATE_FORMAT_EXPANDED}
                            locale="it"
                        />
                    </p>
                </div>
            ) : null}
            <div className="mt-8">
                {!courseQuiz?.timeClose || new Date(courseQuiz.timeClose).getTime() > Date.now() ? (
                    <Button
                        variant="primary"
                        onClick={handleStartQuiz}
                        className="transition-all duration-200 hover:scale-[1.02]"
                    >
                        Inizia il quiz
                    </Button>
                ) : null}
            </div>
            <hr className="my-6 border-[var(--color-mockup-border)]" />
            <div className="text-left">
                <h3 className="mb-3 text-heading-3 font-medium text-[var(--color-mockup-text)]">Tentativi</h3>
                <List>
                    {userAttempts.length ? (
                        userAttempts.map((userAttempt) => (
                            <UserAttemptRow
                                key={userAttempt.id}
                                quizAttempt={{ ...userAttempt, courseQuiz }}
                                onOpen={(quizAttempt) => navigate(`/quiz-attempts/${quizAttempt.id}`)}
                            />
                        ))
                    ) : (
                        <ListItem className="py-4">
                            <ListItemText
                                primary={
                                    <span className="text-body text-[var(--color-mockup-text-mid)]">
                                        <Translate contentKey="edocendoApp.edoQuizAttempt.home.notFound" />
                                    </span>
                                }
                            />
                        </ListItem>
                    )}
                </List>
            </div>
        </Card>
    );
};

export default StartQuizRoute;
